import {
  WORLD_WIDTH, WORLD_HEIGHT,
  SHIP_NOSE_OFFSET,
  BULLET_RADIUS
} from 'asteroids-shared/constants'
import Bullet from 'asteroids-shared/entities/Bullet'
import { bulletSpawnKinematicsFromShip } from 'asteroids-shared/entities/Bullet'
import { refillFireTokens } from 'asteroids-shared/entities/Ship'
import { rotatePoint, wrappedDelta } from 'asteroids-shared/helpers'
import { wrap } from 'asteroids-shared/physics'
import { bulletDebugLocalSpawn, bulletDebugServerSpawn, isGameDebugEnabled } from '../debug/Debug.js'

const DEFAULT_BOUNDS = { width: WORLD_WIDTH, height: WORLD_HEIGHT, toroidalMap: true }

// Local predicted bullets: spawn, tick, server pairing, spawn-time draw offset vs ShipSim.renderedPose. ClientGame constructs.
export default class BulletSim {
  #pairedServerBulletIds = new Set()

  constructor(opts) {
    this.shipSim = opts.shipSim
    this.worldBounds = opts.worldBounds ?? DEFAULT_BOUNDS
    this.socket = opts.socket
    this.interp = opts.interp
    this.getClientSimTick = opts.getClientSimTick
    this.audio = opts.audio
    this.localBullets = []
    this.nextLocalBulletId = -1
    this.spawnAnchors = new Map()
  }

  #deltaX(a, b) {
    return this.worldBounds.toroidalMap === false
      ? a - b
      : wrappedDelta(a, b, this.worldBounds.width)
  }

  #deltaY(a, b) {
    return this.worldBounds.toroidalMap === false
      ? a - b
      : wrappedDelta(a, b, this.worldBounds.height)
  }

  resetOnLocalDeath() {
    this.localBullets = []
    this.#pairedServerBulletIds.clear()
    this.spawnAnchors.clear()
  }

  /**
   * @param {{ firePresses?: number }} keys
   * @param {number} inputSeq
   */
  maybeSpawnLocalBullet(keys, inputSeq) {
    let presses = (keys.firePresses | 0)
    if (presses <= 0 || !this.shipSim.ship.alive || !this.shipSim.initialized) return

    const serverNow = this.socket.serverNow()
    if (!Number.isFinite(serverNow)) return

    const ship = this.shipSim.ship
    refillFireTokens(ship, serverNow)

    while (presses-- > 0 && ship.fireTokens >= 1) {
      ship.fireTokens -= 1
      ship.lastShotAt = serverNow
      this.#spawnOneLocalBullet(ship, inputSeq, serverNow)
    }
  }

  #spawnOneLocalBullet(ship, inputSeq, serverNow) {
    const firedAtPerfMs = performance.now()
    this.audio.playBulletShot()

    const renderedPose = this.shipSim.renderedPose()
    const s = bulletSpawnKinematicsFromShip(ship)
    const offRp = rotatePoint(
      { x: 0, y: -SHIP_NOSE_OFFSET },
      { x: 0, y: 0 },
      renderedPose.rotation * Math.PI / 180
    )
    const visX = renderedPose.x + offRp.x
    const visY = renderedPose.y + offRp.y
    // Sim spawn vs rendered nose (constant for this slug's lifetime).
    const spawnDrawOffset = {
      x: this.#deltaX(visX, s.x),
      y: this.#deltaY(visY, s.y)
    }
    const bullet = new Bullet({
      id: this.nextLocalBulletId--,
      ownerId: ship.id,
      position: { x: s.x, y: s.y },
      velocity: { x: s.vx, y: s.vy },
      rotation: s.rotation
    })
    const entry = {
      bullet,
      serverId: null,
      firedAtInputSeq: inputSeq,
      firedAtPerfMs,
      firedAtSimTick: this.getClientSimTick(),
      spawnAtFire: {
        x: bullet.position.x,
        y: bullet.position.y,
        vx: bullet.velocity.x,
        vy: bullet.velocity.y
      },
      spawnDrawOffset
    }
    this.localBullets.push(entry)
    bulletDebugLocalSpawn({
      localBulletId: bullet.id,
      inputSeq,
      serverNowMs: serverNow,
      x: bullet.position.x,
      y: bullet.position.y,
      vx: bullet.velocity.x,
      vy: bullet.velocity.y,
      shipRotation: ship.rotation,
      renderedRotation: renderedPose.rotation,
      spawnDrawOffset
    })
  }

  updateLocalBullets() {
    this.localBullets = this.localBullets.filter((entry) => {
      if (entry.bullet.dead) return false
      entry.bullet.update(1, this.worldBounds)
      return !entry.bullet.dead
    })
  }

  /**
   * @param {object} m spawn_bullet message
   */
  onServerSpawnBullet(m) {
    if (m.ownerId !== this.shipSim.ship.id) return

    const pendings = this.localBullets.filter((e) => e.serverId == null)
    const pending = this.#bestPendingForSpawnMessage(m, pendings)
    let matchScore = null
    if (pending) {
      const s = pending.spawnAtFire
      const dx = this.#deltaX(m.x, s.x)
      const dy = this.#deltaY(m.y, s.y)
      const dvx = m.vx - s.vx
      const dvy = m.vy - s.vy
      matchScore = dx * dx + dy * dy + (dvx * dvx + dvy * dvy) * .25
      bulletDebugServerSpawn({
        path: 'paired',
        id: m.id,
        localBulletId: pending.bullet.id,
        tick: m.tick,
        serverTime: m.serverTime,
        x: m.x,
        y: m.y,
        vx: m.vx,
        vy: m.vy,
        pendingCount: pendings.length,
        matchScore,
        spawnDx: Math.round(dx * 1000) / 1000,
        spawnDy: Math.round(dy * 1000) / 1000,
        serverInputSeq: m.inputSeq,
        localInputSeq: pending.firedAtInputSeq
      })
      pending.serverId = m.id
      this.#pairedServerBulletIds.add(m.id)
      pending.bullet.id = m.id
      this.#recordSpawnAnchor(m)
      return
    }

    bulletDebugServerSpawn({
      path: 'synthetic',
      id: m.id,
      tick: m.tick,
      serverTime: m.serverTime,
      x: m.x,
      y: m.y,
      vx: m.vx,
      vy: m.vy,
      pendingCount: pendings.length,
      serverInputSeq: m.inputSeq,
      note: 'no pending local bullet; server-only or missed local spawn'
    })
    const bullet = new Bullet({
      id: m.id,
      ownerId: m.ownerId,
      position: { x: m.x, y: m.y },
      velocity: { x: m.vx, y: m.vy },
      rotation: m.rotation,
      fireInputSeq: typeof m.inputSeq === 'number' && Number.isFinite(m.inputSeq) ? m.inputSeq : undefined
    })
    this.#pairedServerBulletIds.add(m.id)
    this.localBullets.push({
      bullet,
      serverId: m.id,
      firedAtPerfMs: performance.now(),
      firedAtSimTick: this.getClientSimTick(),
      spawnAtFire: {
        x: bullet.position.x,
        y: bullet.position.y,
        vx: bullet.velocity.x,
        vy: bullet.velocity.y
      }
    })
    this.#recordSpawnAnchor(m)
  }

  onServerDestroyBullet(m) {
    this.#pairedServerBulletIds.delete(m.id)
    this.spawnAnchors.delete(m.id)
    this.localBullets = this.localBullets.filter((entry) => entry.serverId !== m.id)
  }

  #recordSpawnAnchor(m) {
    if (m.ownerId !== this.shipSim.ship.id) return
    const st = m.serverTime
    if (!Number.isFinite(st)) return
    const cap = this.interp.latest()?.serverTime ?? null
    /**
     * Spawn `serverTime` can run slightly ahead of the newest pushed snapshot; the playhead stays
     * at or below `latest` until the next snapshot, so `bulletPositionAt` would freeze at the nose.
     * Clamp the anchor epoch to drawable snapshot time.
     */
    const serverTime = Number.isFinite(cap) ? Math.min(st, cap) : st
    const redSlugSpawnOffset = this.#redSlugSpawnOffsetForDebug(m, serverTime)
    this.spawnAnchors.set(m.id, {
      serverTime,
      perfAtPair: performance.now(),
      serverNowAtPair: this.socket.serverNow(),
      pairSimTick: this.getClientSimTick(),
      x: m.x,
      y: m.y,
      vx: m.vx ?? 0,
      vy: m.vy ?? 0,
      redSlugSpawnOffset,
      ...(typeof m.inputSeq === 'number' && Number.isFinite(m.inputSeq) ? { inputSeq: m.inputSeq } : {})
    })
  }

  /** Interp nose at `serverTime` minus server spawn — shifts red debug slug to leave the red ring nose. */
  #redSlugSpawnOffsetForDebug(m, serverTime) {
    if (!isGameDebugEnabled()) return { x: 0, y: 0 }
    if (!Number.isFinite(serverTime)) return { x: 0, y: 0 }
    const view = this.interp.sample(serverTime)
    const ship = view.ships.find(s => String(s.id) === String(m.ownerId))
    if (!ship?.alive) return { x: 0, y: 0 }
    if (ship.rotation == null || !Number.isFinite(ship.rotation)) return { x: 0, y: 0 }
    const off = rotatePoint(
      { x: 0, y: -SHIP_NOSE_OFFSET },
      { x: 0, y: 0 },
      ship.rotation * Math.PI / 180
    )
    const nx = ship.x + off.x
    const ny = ship.y + off.y
    return {
      x: this.#deltaX(nx, m.x),
      y: this.#deltaY(ny, m.y)
    }
  }

  /**
   * Pair `spawn_bullet` to the pending local shot it belongs to (not FIFO):
   * events can arrive out of order vs multiple pendings while spinning.
   */
  #bestPendingForSpawnMessage(m, pendings) {
    if (pendings.length === 0) return null
    const serverSeq = m.inputSeq
    if (typeof serverSeq === 'number' && Number.isFinite(serverSeq)) {
      const seqMatches = pendings.filter((e) => e.firedAtInputSeq === serverSeq)
      if (seqMatches.length === 1) return seqMatches[0]
      if (seqMatches.length > 1) return this.#lowestSpawnMatchScorePending(m, seqMatches)
    }
    if (pendings.length === 1) return pendings[0]
    return this.#lowestSpawnMatchScorePending(m, pendings)
  }

  #lowestSpawnMatchScorePending(m, pendings) {
    let best = pendings[0]
    let bestScore = Infinity
    for (const e of pendings) {
      const s = e.spawnAtFire
      const dx = this.#deltaX(m.x, s.x)
      const dy = this.#deltaY(m.y, s.y)
      const dvx = m.vx - s.vx
      const dvy = m.vy - s.vy
      const score = dx * dx + dy * dy + (dvx * dvx + dvy * dvy) * .25
      if (score < bestScore) {
        bestScore = score
        best = e
      }
    }
    return best
  }

  /**
   * Display position: sim state + constant spawn-time offset (drawn nose vs sim spawn).
   * No decay — a decaying offset can outrun sim velocity and jump backward at large magnitudes.
   */
  bulletDrawPosition(entry) {
    const b = entry.bullet
    const o = entry.spawnDrawOffset
    const p = {
      x: b.position.x + (o ? o.x : 0),
      y: b.position.y + (o ? o.y : 0)
    }
    if (this.worldBounds.toroidalMap !== false) {
      wrap(p, this.worldBounds.width, this.worldBounds.height, BULLET_RADIUS)
    }
    return p
  }

}

/** @typedef {{
 *   bullet: object,
 *   serverId: number | null,
 *   spawnAtFire: { x: number, y: number, vx: number, vy: number },
 *   firedAtInputSeq?: number,
 *   firedAtPerfMs?: number,
 *   firedAtSimTick?: number,
 *   spawnDrawOffset?: { x: number, y: number }
 * }} BulletEntry */
